

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  ArrowLeft,
  FileText,
  Video,
  BookOpen,
  Download,
  Play,
  Calendar,
  Clock,
  ExternalLink,
  Loader2,
  CheckCircle,
  AlertCircle,
  Globe,
  Briefcase,
  Calculator
} from 'lucide-react';
import EventRegistrationModal from '@/components/EventRegistrationModal';
import { getUpcomingWebinars, getWebinarRecordings, Webinar } from '@/services/api/webinarManagement';
import { subscribeToNewsletter } from '@/services/api/eventRegistrations';
import { getWeaveinApiUrl } from '@/src/config';
import { getApiBaseUrl } from '@/lib/utils';
import usePageMeta from '@/hooks/usePageMeta';
import { SEO_ROUTES } from '@/src/seo/routes';
import MarketingHeader from '@/components/marketing/MarketingHeader';
import MarketingFooter from '@/components/marketing/MarketingFooter';
import { BENCHMARK_BASELINE, ENGAGEMENT, ENGAGEMENT_MULTIPLE, contactHref } from '@/components/marketing/siteFacts';

// Helper to resolve media URLs (handles both absolute and relative paths)
const resolveMediaUrl = (url: string | null): string | null => {
  if (!url) return null;
  // If already absolute URL, return as-is
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  // If relative path, prepend the API base URL
  const baseUrl = getWeaveinApiUrl();
  return `${baseUrl}${url.startsWith('/') ? '' : '/'}${url}`;
};

// Resource post interface from API
interface ResourcePost {
  id: number;
  title: string;
  description: string | null;
  resource_type: 'article' | 'video' | 'document' | 'webinar' | 'guide' | 'weblink';
  source_type: string | null;
  source_id: number | null;
  external_url: string | null;
  thumbnail_url: string | null;
  is_featured: boolean;
  published_at: string | null;
  category: string | null;
  tags: string[];
  view_count: number;
}

/**
 * Resources Page
 * Blog, case studies, webinars, and downloadable content
 * Dynamically fetches upcoming webinars and recordings from the API
 */
export default function ResourcesPage() {
  usePageMeta(SEO_ROUTES['/resources']);

  const currentYear = new Date().getFullYear();
  const [selectedEvent, setSelectedEvent] = useState<{
    id: string;
    title: string;
    date: string;
    time: string;
  } | null>(null);

  // Dynamic webinar data
  const [upcomingWebinars, setUpcomingWebinars] = useState<Webinar[]>([]);
  const [recordings, setRecordings] = useState<Webinar[]>([]);
  const [loadingWebinars, setLoadingWebinars] = useState(true);

  // Dynamic resource posts from API
  const [dynamicResources, setDynamicResources] = useState<ResourcePost[]>([]);
  const [loadingResources, setLoadingResources] = useState(true);

  // Newsletter subscription state
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [newsletterStatus, setNewsletterStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [newsletterMessage, setNewsletterMessage] = useState('');


  useEffect(() => {
    loadWebinars();
    loadPublicResources();
  }, []);

  const loadPublicResources = async () => {
    try {
      setLoadingResources(true);
      const response = await fetch(`${getApiBaseUrl()}/api/resources/public/posts`);
      if (response.ok) {
        const data = await response.json();
        setDynamicResources(data.posts || []);
      }
    } catch (err) {
      console.error('Failed to load public resources:', err);
    } finally {
      setLoadingResources(false);
    }
  };

  const loadWebinars = async () => {
    try {
      setLoadingWebinars(true);
      const [upcomingResponse, recordingsResponse] = await Promise.all([
        getUpcomingWebinars(false),
        getWebinarRecordings()
      ]);
      setUpcomingWebinars(upcomingResponse.upcoming || []);
      setRecordings(recordingsResponse.recordings || []);
    } catch (err) {
      console.error('Failed to load webinars:', err);
      // Fallback to static data
      setUpcomingWebinars([]);
      setRecordings([]);
    } finally {
      setLoadingWebinars(false);
    }
  };

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!newsletterEmail || !newsletterEmail.includes('@')) {
      setNewsletterStatus('error');
      setNewsletterMessage('Please enter a valid email address.');
      return;
    }

    setNewsletterStatus('loading');
    try {
      const response = await subscribeToNewsletter(newsletterEmail);
      setNewsletterStatus('success');
      setNewsletterMessage(response.message);
      if (!response.already_subscribed) {
        setNewsletterEmail(''); // Clear input on new subscription
      }
    } catch (err) {
      setNewsletterStatus('error');
      setNewsletterMessage('Failed to subscribe. Please try again.');
      console.error('Newsletter subscription error:', err);
    }
  };

  const resources = [
    // Evidence lives on /impact; one card points there instead of copying it
    {
      type: 'Impact',
      title: 'What happened when people used it',
      description: `Medi-Cal outreach across four CBO federations: ${ENGAGEMENT.value} engagement, ${ENGAGEMENT_MULTIPLE.value} the ${BENCHMARK_BASELINE}% CHCS benchmark, and the programs behind each number.`,
      icon: BookOpen,
      cta: 'See the evidence',
      link: '/impact',
      color: 'green',
      metrics: [`${ENGAGEMENT.value} engagement`, `${ENGAGEMENT_MULTIPLE.value} benchmark`]
    },
    // Guides
    {
      type: 'Guide',
      title: 'The Employer\'s Guide to SDOH',
      description: 'A guide for HR leaders on how social determinants show up in healthcare costs, turnover and productivity, with strategies, an ROI model you fill in with your own numbers, and an implementation roadmap.',
      icon: FileText,
      cta: 'Request Guide',
      link: contactHref('guide', 'Employer guide to SDOH'),
      color: 'blue',
      metrics: ['ROI models', 'Implementation guide']
    },
    {
      type: 'Guide',
      title: 'Health Plan Integration Playbook',
      description: `Step-by-step playbook for health plans to improve member engagement through community partnerships: the approach behind ${ENGAGEMENT.value} cohort engagement (${ENGAGEMENT_MULTIPLE.value} the ${BENCHMARK_BASELINE}% CHCS benchmark), integration with ECM and Community Supports, and how to report delivery to regulators.`,
      icon: FileText,
      cta: 'Request Playbook',
      link: contactHref('guide', 'Health plan integration playbook'),
      color: 'blue',
      metrics: [`${ENGAGEMENT.value} engagement`, 'ECM/CS integration']
    },
    // Webinar
    {
      type: 'Webinar',
      title: 'SDOH 101: What HR Leaders Need to Know',
      description: 'A 30-minute on-demand webinar covering what social determinants are, how they show up in a workforce, and how employers are addressing them to reduce costs and improve retention. Featuring case studies and Q&A.',
      icon: Video,
      cta: 'Request the recording',
      link: contactHref('guide', 'SDOH 101 webinar recording'),
      color: 'purple',
      metrics: ['30 minutes', 'On-demand']
    },
    // Calculator
    {
      type: 'Calculator',
      title: 'SDOH ROI Calculator',
      description: 'Interactive calculator to estimate your potential savings from addressing social determinants. Input your employee count, average salary, turnover rates, and at-risk population to see projected ROI, cost savings, and retention improvements.',
      icon: Download,
      cta: 'Try Calculator',
      link: '/roi-calculator',
      color: 'orange',
      metrics: ['Interactive', 'Instant results']
    }
  ];

  const colorClasses = {
    blue: 'bg-blue-100 dark:bg-blue-900/30 text-blue-600',
    green: 'bg-green-100 dark:bg-green-900/30 text-green-600',
    purple: 'bg-v2accent-soft text-v2accent-ink',
    orange: 'bg-orange-100 dark:bg-orange-900/30 text-orange-600'
  };

  // Track which thumbnails failed to load
  const [failedThumbnails, setFailedThumbnails] = useState<Set<number>>(new Set());

  // Helper to get icon and color for dynamic resource types
  // Icons match the static resources section for consistency
  const getResourceTypeConfig = (type: string) => {
    switch (type) {
      case 'article':
        // Articles use BookOpen with green - matches Case Study styling
        return { icon: BookOpen, color: 'green', label: 'Article' };
      case 'video':
        // Videos use Video icon with purple
        return { icon: Video, color: 'purple', label: 'Video' };
      case 'document':
        // Documents use FileText with blue - matches Guide styling
        return { icon: FileText, color: 'blue', label: 'Document' };
      case 'webinar':
        // Webinars use Video icon with purple (similar to video content)
        return { icon: Video, color: 'purple', label: 'Webinar' };
      case 'guide':
        // Guides use FileText with blue - matches static Guide resources
        return { icon: FileText, color: 'blue', label: 'Guide' };
      case 'weblink':
        // Web links use Globe with orange
        return { icon: Globe, color: 'orange', label: 'Web Link' };
      default:
        // Default fallback
        return { icon: FileText, color: 'blue', label: 'Resource' };
    }
  };

  // Handle thumbnail load error - show fallback icon instead
  const handleThumbnailError = (resourceId: number) => {
    setFailedThumbnails(prev => new Set(prev).add(resourceId));
  };

  // Only real upcoming events. When there are none the page says so, rather
  // than showing hard-coded January 2025 events as "upcoming".
  const displayEvents = upcomingWebinars.map(w => ({
    id: w.event_id,
    title: w.title,
    date: w.date || '',
    time: w.time || ''
  }));

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      {/* Header */}
      <MarketingHeader />

      {/* Hero Section */}
      <section className="bg-paper border-b border-v2border py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-ink mb-6">
            Resources
          </h1>
          <p className="text-xl text-ink-500 max-w-2xl mx-auto">
            Guides, case studies, webinars, and tools to help you understand
            and address social determinants of health in your population.
          </p>
        </div>
      </section>

      {/* Resources Grid */}
      <section className="py-20 bg-white dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loadingResources ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-ink" />
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {/* Dynamic resources from API (shown first) */}
              {dynamicResources.map((resource) => {
                const config = getResourceTypeConfig(resource.resource_type);
                const Icon = config.icon;
                const thumbnailSrc = resolveMediaUrl(resource.thumbnail_url);
                const showThumbnail = thumbnailSrc && !failedThumbnails.has(resource.id);
                return (
                  <div key={`dynamic-${resource.id}`} className="bg-gray-50 dark:bg-gray-800 rounded-xl p-6 hover:shadow-lg transition-shadow">
                    {showThumbnail ? (
                      <div className="w-full h-40 rounded-lg overflow-hidden mb-4">
                        <img
                          src={thumbnailSrc}
                          alt={resource.title}
                          className="w-full h-full object-cover"
                          onError={() => handleThumbnailError(resource.id)}
                        />
                      </div>
                    ) : (
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${colorClasses[config.color as keyof typeof colorClasses]}`}>
                        <Icon className="w-6 h-6" />
                      </div>
                    )}
                    <div className="text-sm text-v2accent font-semibold mb-2">{config.label}</div>
                    <h3 className="text-xl font-bold text-ink dark:text-white mb-3">
                      {resource.title}
                    </h3>
                    {resource.description && (
                      <p className="text-gray-600 dark:text-gray-300 text-sm mb-4">
                        {resource.description}
                      </p>
                    )}
                    {resource.external_url ? (
                      <a
                        href={resource.external_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center text-ink dark:text-white font-semibold hover:text-v2accent"
                      >
                        View Resource
                        <ExternalLink className="w-4 h-4 ml-2" />
                      </a>
                    ) : (
                      <span className="inline-flex items-center text-ink dark:text-white font-semibold">
                        View Resource
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </span>
                    )}
                  </div>
                );
              })}

              {/* Static fallback resources */}
              {resources.map((resource, index) => {
                const Icon = resource.icon;
                return (
                  <div key={`static-${index}`} className="bg-gray-50 dark:bg-gray-800 rounded-xl p-6 hover:shadow-lg transition-shadow">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${colorClasses[resource.color as keyof typeof colorClasses]}`}>
                      <Icon className="w-6 h-6" />
                    </div>
                    <div className="text-sm text-v2accent font-semibold mb-2">{resource.type}</div>
                    <h3 className="text-xl font-bold text-ink dark:text-white mb-3">
                      {resource.title}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-300 text-sm mb-4">
                      {resource.description}
                    </p>
                    {resource.link.startsWith('http') ? (
                      <a
                        href={resource.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center text-ink dark:text-white font-semibold hover:text-v2accent"
                      >
                        {resource.cta}
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </a>
                    ) : (
                      <Link
                        to={resource.link}
                        className="inline-flex items-center text-ink dark:text-white font-semibold hover:text-v2accent"
                      >
                        {resource.cta}
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Link>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Upcoming Events */}
      <section className="py-20 bg-gray-50 dark:bg-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-ink dark:text-white mb-4">
              Upcoming Events
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-300">
              Join us for live webinars and workshops on SDOH.
            </p>
          </div>

          <div className="max-w-3xl mx-auto">
            {loadingWebinars ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-8 h-8 animate-spin text-ink" />
              </div>
            ) : displayEvents.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No upcoming events scheduled. Check back soon!
              </div>
            ) : (
              displayEvents.map((event, index) => (
                <div key={index} className="bg-white dark:bg-gray-900 rounded-xl p-6 mb-4 flex items-center justify-between shadow-md">
                  <div className="flex items-center">
                    <div className="w-12 h-12 bg-v2accent rounded-xl flex items-center justify-center mr-4">
                      <Calendar className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h4 className="font-bold text-ink dark:text-white">{event.title}</h4>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {event.date} {event.time && `• ${event.time}`}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setSelectedEvent(event)}
                    className="px-4 py-2 bg-ink text-paper rounded-lg hover:bg-ink-700 transition-colors font-medium text-sm"
                  >
                    Register
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
      </section>

      {/* Webinar Recordings */}
      {recordings.length > 0 && (
        <section className="py-20 bg-white dark:bg-gray-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-ink dark:text-white mb-4">
                On-Demand Webinars
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-300">
                Watch our past webinars at your convenience.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {recordings.map((recording, index) => (
                <div key={index} className="bg-gray-50 dark:bg-gray-800 rounded-xl overflow-hidden hover:shadow-lg transition-shadow">
                  {/* Thumbnail */}
                  <div className="relative h-48 bg-v2accent-soft">
                    {recording.recording_thumbnail_url ? (
                      <img
                        src={recording.recording_thumbnail_url}
                        alt={recording.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <Video className="w-16 h-16 text-v2accent" />
                      </div>
                    )}
                    {/* Play button overlay */}
                    <a
                      href={recording.recording_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="absolute inset-0 flex items-center justify-center bg-black/30 hover:bg-black/40 transition-colors"
                    >
                      <div className="w-16 h-16 bg-white/90 rounded-full flex items-center justify-center">
                        <Play className="w-8 h-8 text-ink ml-1" />
                      </div>
                    </a>
                    {/* Duration badge */}
                    {recording.recording_duration_minutes && (
                      <div className="absolute bottom-2 right-2 bg-black/70 text-white text-xs px-2 py-1 rounded flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {recording.recording_duration_minutes} min
                      </div>
                    )}
                  </div>
                  {/* Content */}
                  <div className="p-5">
                    <div className="text-sm text-v2accent font-semibold mb-1">
                      {recording.event_date}
                    </div>
                    <h3 className="text-lg font-bold text-ink dark:text-white mb-2">
                      {recording.title}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-300 text-sm mb-4 line-clamp-2">
                      {recording.description}
                    </p>
                    <a
                      href={recording.recording_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center text-ink dark:text-white font-semibold hover:text-v2accent"
                    >
                      Watch Now
                      <ExternalLink className="w-4 h-4 ml-2" />
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Newsletter */}
      <section className="py-20 bg-paper border-b border-v2border">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-ink mb-4">
            Stay Updated
          </h2>
          <p className="text-xl text-ink-500 mb-8">
            Get the latest insights on SDOH delivered to your inbox.
          </p>
          <form onSubmit={handleNewsletterSubmit} className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
            <input
              type="email"
              value={newsletterEmail}
              onChange={(e) => {
                setNewsletterEmail(e.target.value);
                if (newsletterStatus !== 'idle') {
                  setNewsletterStatus('idle');
                  setNewsletterMessage('');
                }
              }}
              placeholder="Enter your email"
              disabled={newsletterStatus === 'loading'}
              className="flex-1 px-4 py-3 rounded-lg bg-v2accent-soft border border-v2border text-ink placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-v2accent disabled:opacity-50"
            />
            <button
              type="submit"
              disabled={newsletterStatus === 'loading'}
              className="px-6 py-3 bg-ink text-paper font-semibold rounded-lg hover:bg-ink-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {newsletterStatus === 'loading' ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Subscribing...
                </>
              ) : (
                'Subscribe'
              )}
            </button>
          </form>
          {newsletterMessage && (
            <div className={`mt-4 flex items-center justify-center gap-2 text-sm ${
 newsletterStatus === 'success' ? 'text-green-300' : 'text-red-300'
 }`}>
              {newsletterStatus === 'success' ? (
                <CheckCircle className="w-4 h-4" />
              ) : (
                <AlertCircle className="w-4 h-4" />
              )}
              {newsletterMessage}
            </div>
          )}
        </div>
      </section>

      {/* Footer */}
      <MarketingFooter />

      {/* Event Registration Modal */}
      {selectedEvent && (
        <EventRegistrationModal
          isOpen={!!selectedEvent}
          onClose={() => setSelectedEvent(null)}
          event={selectedEvent}
        />
      )}
    </div>
  );
}
